import { Box, Button, Divider, Grid, Link, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { CardProps } from "..";
import { PaperBox } from "../../../../../../components/Layout/PaperBox";
import { useCallback, useMemo } from "react";
import StepPagamento from "./StepPagamento";
import StepExpedicao from "./StepExpedicao";
import StepAprovacao from "./StepAprovacao";
import { DadoValorPrinter } from "../../../../../../components/BasicComponents/DisplayDados/DadoValorPrinter";
import { useModalDetalheProcesso } from "../../../../../../components/ModalContexts/ModalDetalheProcessoContext";
import { confirm } from "material-ui-confirm";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { usePermission } from "../../../../../../components/PermissionGuard";

export default function CardSituacaoPedido({
    data
}: PropsCardSituacaoPedido) {

    const { openModal } = useModalDetalheProcesso();
    const queryClient = useQueryClient();

    //Querys e mutations
    const { mutate: cancelar, isLoading: isLoadingCancelar } = useMutation({
        mutationFn: (id: number) =>
            axiosClient.post(`comercial/pedidos/cancelar-pedido/${id}`),
        onError: (error) => {
            console.error(error);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(`comercial/detalhar-pedido`);
            enqueueSnackbar("Pedido cancelado com sucesso", { variant: "success" });
        },
    });

    //Funções manipuladoras
    const handleCancelar = useCallback(
        async (id: number) => {
            confirm({ description: "Deseja cancelar o pedido?" })
                .then(() => cancelar(id))
                .catch(() => { });
        }, []);

    // Dados importantes
    const steps = useMemo(() => [
        'Pedido criado', //0
        'Aprovação', //1
        'Pagamento', //2
        'Expedição', //3
        'Finalizado' //4
    ], []);

    const indexSteps = useMemo(() => {
        return steps.findIndex(i => i === data.situacao)
    }, [steps, data.situacao])

    const podeCancelar = useMemo(() => {
        return ((data.historico_expedicao && data.historico_expedicao.some(i => i.possui_nota_fiscal))) || (data.situacao === "Reprovado" || data.situacao === "Cancelado");
    }, [data])

    const permissao = usePermission('comercial.cancelar-pedido');
    
    return (
        <PaperBox>
            <Stack direction="row" justifyContent={'space-between'} alignItems={'flex-start'} mb={1}>
                <Typography variant="h6" mb={2}>Situação do pedido</Typography>
                <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleCancelar(data.id)}
                    disabled={isLoadingCancelar || podeCancelar || !permissao}
                >
                    Cancelar
                </Button>
            </Stack>
            <>
                {
                    data.situacao === "Reprovado" && data.processo &&
                    <Box sx={{ mx: 2, mb: 2 }}>
                        <Divider />
                        <Typography variant="subtitle1" fontWeight="medium" align="center" sx={{ my: 1 }} >
                            Pedido reprovado
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid size={{xs: 12, md: 4}}>
                                <Stack>
                                    <Typography fontWeight="bold" variant="subtitle2">Código do Processo</Typography>
                                    <Link
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            openModal(data.processo?.codigo ?? '')
                                        }}>
                                        <Typography variant="subtitle2">#{data.processo.codigo}</Typography>
                                    </Link>
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 12, md: 4}}>
                                <DadoValorPrinter
                                    titulo="Status Atual"
                                    valor={data.processo.situacao}
                                />
                            </Grid>
                            <Grid size={{xs: 12, md: 4}}>
                                <DadoValorPrinter
                                    titulo=" Tarefa Atual"
                                    valor={data.processo.tarefa_atual}
                                />
                            </Grid>
                            <Grid size={{xs: 12}}>
                                <DadoValorPrinter
                                    titulo="Motivo da reprovação"
                                    valor={data.processo.ultima_tarefa.observacoes}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                }
                {
                    data.situacao !== "Reprovado" &&
                    <Grid container>
                        <Grid size={{xs: 12}}>
                            <Stepper activeStep={indexSteps} alternativeLabel>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Grid>
                        <Grid size={{xs: 12}}>
                            {
                                data.situacao === "Aprovação" &&
                                <StepAprovacao {...{ data }} />
                            }
                            {
                                data.situacao === "Pagamento" &&
                                <StepPagamento {...{ data }} />
                            }
                            {
                                data.situacao === "Expedição" &&
                                <StepExpedicao {...{ data }} />
                            }
                        </Grid>
                    </Grid>
                }
            </>
        </PaperBox>
    )
}

interface PropsCardSituacaoPedido extends CardProps { }
